/**
 * What a person may do in the club they act in. The server decides (App\Enum\Permission,
 * sent as `permissions` by GET /api/me); the app only shows or hides screens with it.
 * Ask `can(user, 'members.edit')`, never the role: a coach may have been granted more.
 * The server stays the judge, so a hidden button is a convenience, not protection.
 */
export type Permission =
  | 'members.view'
  | 'members.edit'
  | 'members.import'
  | 'members.delete'
  | 'enrollments.manage'
  | 'registrations.review'
  | 'registrations.configure'
  | 'planning.view'
  | 'planning.manage'
  | 'classes.manage_all'
  | 'classes.manage_own'
  | 'attendance.take'
  | 'attendance.correct'
  | 'devices.manage'
  | 'curriculum.manage'
  | 'grades.award'
  | 'badges.manage'
  | 'events.manage'
  | 'club.settings'
  | 'staff.manage'
  | 'reports.view'
  | 'audit.view';

/** The permissions as the staff page lists them, by domain. Every permission appears once. */
export const PERMISSION_GROUPS: readonly { key: string; permissions: readonly Permission[] }[] = [
  { key: 'members', permissions: ['members.view', 'members.edit', 'members.import', 'members.delete', 'enrollments.manage'] },
  { key: 'registrations', permissions: ['registrations.review', 'registrations.configure'] },
  { key: 'planning', permissions: ['planning.view', 'planning.manage', 'classes.manage_all', 'classes.manage_own'] },
  { key: 'attendance', permissions: ['attendance.take', 'attendance.correct', 'devices.manage'] },
  { key: 'teaching', permissions: ['curriculum.manage', 'grades.award', 'badges.manage', 'events.manage'] },
  { key: 'club', permissions: ['club.settings', 'staff.manage'] },
  { key: 'oversight', permissions: ['reports.view', 'audit.view'] },
];

interface PermissionHolder {
  permissions?: Permission[];
  isStaff?: boolean;
}

export function can(user: PermissionHolder | null | undefined, permission: Permission): boolean {
  return user?.permissions?.includes(permission) ?? false;
}

export function canAny(user: PermissionHolder | null | undefined, permissions: readonly Permission[]): boolean {
  return permissions.some((permission) => can(user, permission));
}

/** Runs the club in some way (owner, admin or coach, or a break-glass visitor): opens the staff workspaces. */
export function isStaff(user: PermissionHolder | null | undefined): boolean {
  return user?.isStaff ?? false;
}

/**
 * The admin workspace: a screen to land on for each permission that belongs to it, best landing
 * first. A person holding any of them gets the workspace, next to the coach workspace every staff
 * member has. What a coach works with every day (enrollments, own classes, attendance, curriculum)
 * lives in the coach workspace instead. Each path must be a route that asks for that same permission,
 * or the redirect away from a screen the person may not open would loop.
 */
const ADMIN_LANDINGS: readonly (readonly [Permission, string])[] = [
  ['reports.view', '/admin'],
  ['members.view', '/admin/students'],
  ['planning.view', '/admin/planning'],
  ['registrations.review', '/admin/inscriptions'],
  ['attendance.correct', '/admin/presences'],
  ['planning.manage', '/admin/lieux'],
  ['badges.manage', '/admin/badges'],
  ['events.manage', '/admin/events'],
  ['club.settings', '/admin/club'],
  ['registrations.configure', '/admin/inscriptions/reglages'],
  ['members.import', '/admin/imports'],
  ['audit.view', '/admin/audit-log'],
];

export function opensAdminWorkspace(user: PermissionHolder | null | undefined): boolean {
  return adminHome(user) !== null;
}

/** Where the admin workspace opens for this person, or null when they do not have it. */
export function adminHome(user: PermissionHolder | null | undefined): string | null {
  if (!isStaff(user)) return null;
  return ADMIN_LANDINGS.find(([permission]) => can(user, permission))?.[1] ?? null;
}
